import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { zohoAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Copy, Check, Trash2, Settings2, FileJson, ScrollText, CheckCircle2, Clock, AlertCircle, Ban, BarChart3, LayoutGrid } from 'lucide-react';
import ZohoTestWebhook from '@/components/zoho/ZohoTestWebhook';
import ZohoFieldMapping from '@/components/zoho/ZohoFieldMapping';
import ZohoFormReport from '@/components/zoho/ZohoFormReport';
import ZohoFormLogs from '@/components/zoho/ZohoFormLogs';
import ZohoSubmitterFieldPicker from '@/components/zoho/ZohoSubmitterFieldPicker';
import ZohoSmartDashboardConfig from '@/components/zoho/ZohoSmartDashboardConfig';
import ZohoSmartDashboard from '@/components/zoho/ZohoSmartDashboard';

const STATUS_CFG = {
  active: { variant: 'default', label: 'Active', Icon: CheckCircle2 },
  pending_mapping: { variant: 'secondary', label: 'Pending Mapping', Icon: Clock },
  pending_test: { variant: 'outline', label: 'Pending Test', Icon: AlertCircle },
  disabled: { variant: 'destructive', label: 'Disabled', Icon: Ban },
};

export default function ZohoFormDetail({ form, modules, onFormUpdated, onFormDeleted, onBack }) {
  const [tab, setTab] = useState(form.primary_key_field ? 'dashboard' : 'report');
  const [copied, setCopied] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const webhookUrl = form.slug ? zohoAPI.getWebhookUrl(form.slug) : '(Legacy form — no webhook URL)';
  const sCfg = STATUS_CFG[form.status] || STATUS_CFG.pending_test;

  const copyUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    toast.success('Webhook URL copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await zohoAPI.getFormLogs(form.id, { limit: 100 });
      setLogs(res.data.logs || []);
    } catch { setLogs([]); }
    setLogsLoading(false);
  }, [form.id]);

  useEffect(() => {
    if (tab === 'logs') fetchLogs();
  }, [tab, fetchLogs]);

  const handleDelete = async () => {
    try {
      await zohoAPI.deleteForm(form.id);
      toast.success('Form deleted');
      onFormDeleted(form.id);
    } catch (e) {
      toast.error('Failed to delete form');
    }
    setDelOpen(false);
  };

  const handleTestComplete = async () => {
    try {
      const res = await zohoAPI.getForm(form.id);
      onFormUpdated(res.data);
    } catch {}
  };

  const handleMappingUpdated = (updatedForm) => {
    onFormUpdated(updatedForm);
  };

  return (
    <div className="space-y-4" data-testid="form-detail">
      {/* Form Header Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="text-lg flex items-center gap-2">
                {form.form_name}
                <Badge variant={sCfg.variant} className="gap-1 ml-2">
                  <sCfg.Icon className="h-3 w-3" />{sCfg.label}
                </Badge>
              </CardTitle>
              {form.description && <p className="text-sm text-muted-foreground">{form.description}</p>}
              {form.category_name && (
                <Badge variant="outline" className="text-xs">{form.category_name}</Badge>
              )}
            </div>
            <Button variant="outline" size="sm" className="text-destructive" onClick={() => setDelOpen(true)} data-testid="delete-form-btn">
              <Trash2 className="h-4 w-4 mr-1" />Delete
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Webhook URL */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Webhook URL</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 p-2.5 bg-muted rounded-md text-sm break-all font-mono" data-testid="form-webhook-url">
                {webhookUrl}
              </code>
              <Button variant="outline" size="icon" onClick={copyUrl} data-testid="copy-form-url">
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Test Webhook */}
          <ZohoTestWebhook form={form} onTestComplete={handleTestComplete} />

          {/* Submitter field config */}
          <ZohoSubmitterFieldPicker form={form} onFormUpdated={onFormUpdated} />

          {/* Smart Dashboard Config */}
          <ZohoSmartDashboardConfig form={form} onFormUpdated={onFormUpdated} />

          {/* Quick stats */}
          <div className="flex gap-4 text-sm text-muted-foreground pt-1">
            <span>Submissions: <strong className="text-foreground">{form.submission_count || 0}</strong></span>
            <span>Detected fields: <strong className="text-foreground">{form.detected_fields?.length || 0}</strong></span>
            <span>Mapped fields: <strong className="text-foreground">{form.field_mappings?.length || 0}</strong></span>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {form.primary_key_field && (
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <LayoutGrid className="h-4 w-4 mr-2" />Dashboard
            </TabsTrigger>
          )}
          <TabsTrigger value="report" data-testid="tab-report">
            <BarChart3 className="h-4 w-4 mr-2" />Submissions
          </TabsTrigger>
          <TabsTrigger value="mapping" data-testid="tab-mapping">
            <Settings2 className="h-4 w-4 mr-2" />Field Mapping
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">
            <ScrollText className="h-4 w-4 mr-2" />Logs
          </TabsTrigger>
        </TabsList>

        {form.primary_key_field && (
          <TabsContent value="dashboard" className="mt-4">
            <ZohoSmartDashboard form={form} />
          </TabsContent>
        )}
        <TabsContent value="report" className="mt-4">
          <ZohoFormReport form={form} />
        </TabsContent>
        <TabsContent value="mapping" className="mt-4">
          <ZohoFieldMapping form={form} modules={modules} onMappingUpdated={handleMappingUpdated} />
        </TabsContent>
        <TabsContent value="logs" className="mt-4">
          <ZohoFormLogs logs={logs} loading={logsLoading} onRefresh={fetchLogs} />
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation */}
      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{form.form_name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the form, all its submissions, and webhook logs. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-form">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
